/**
 * Components Viewer
 * Interactive display of complex UI components (Card, Modal, Table, etc.)
 */

import React, { useState } from "react";
import {
  Box,
  Text,
  Heading,
  Button,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Avatar,
  Badge,
  Progress,
  useToast,
  Code,
  useColorModeValue,
} from "@chakra-ui/react";
import CDS, { useResolveColor, useResolveSpacing, useResolveSize } from "design-system";

function CardExamples() {
  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary}>Card Component</CDS.Text>

      <CDS.Grid columns={{ base: 1, md: 2, lg: 3 }} gap={CDS.Spacing.large}>
        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small}>
              <CDS.Text variant={CDS.Text.variant.titleMedium} color={CDS.Color.text.primary}>Simple Card</CDS.Text>
              <CDS.Text variant={CDS.Text.variant.bodySmall} color={CDS.Color.text.secondary}>
                Basic card with minimal styling and content.
              </CDS.Text>
              <Button size="sm" variant="outline">
                Learn More
              </Button>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small}>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
                <Avatar size="sm" name="John Doe" />
                <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.tiny}>
                  <Text fontWeight="semibold" fontSize="sm">John Doe</Text>
                  <Text fontSize="xs" color="gray.600">Product Manager</Text>
                </CDS.Stack>
              </CDS.Stack>
              <Text fontSize="sm">
                Card with user information and avatar component.
              </Text>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
                <Badge colorScheme="green">Active</Badge>
                <Badge variant="outline">Team Lead</Badge>
              </CDS.Stack>
            </CDS.Stack>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <CDS.Stack align={CDS.Stack.align.start} spacing={CDS.Spacing.small}>
              <Heading size="sm">Progress Card</Heading>
              <Box w="full">
                <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between} mb={2}>
                  <Text fontSize="sm">Completion</Text>
                  <Text fontSize="sm" fontWeight="semibold">75%</Text>
                </CDS.Stack>
                <Progress value={75} colorScheme="blue" size="sm" />
              </Box>
              <Text fontSize="sm" color="gray.600">
                Card showing progress with visual indicators.
              </Text>
            </CDS.Stack>
          </CardBody>
        </Card>
      </CDS.Grid>

      <Box>
        <Text fontSize="sm" color="gray.600" fontStyle="italic">
          Code examples:
          {" "}
          <code>{"<CDS.Card elevation={CDS.shadowElevation.medium()}>...</CDS.Card>"}</code>
        </Text>
      </Box>
    </CDS.Stack>
  );
}

function ModalExamples() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Modal Component</Heading>

      <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.medium}>
        <Button onClick={onOpen}>Open Modal</Button>
        <Button variant="outline">Open Large Modal</Button>
        <Button variant="ghost">Open Confirmation</Button>
      </CDS.Stack>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sample Modal</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
              <Text>
                This is an example of the modal component from the design system.
              </Text>
              <Text fontSize="sm" color="gray.600">
                Modals should be used sparingly and only for critical actions or information.
              </Text>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.end} spacing={CDS.Spacing.small}>
                <Button variant="ghost" onClick={onClose}>
                  Cancel
                </Button>
                <Button onClick={onClose}>
                  Confirm
                </Button>
              </CDS.Stack>
            </CDS.Stack>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Box>
        <Text fontSize="sm" color="gray.600" fontStyle="italic">
          Code examples:
          {" "}
          <code>{"<CDS.Modal size={CDS.modalSize.medium()} isOpen={isOpen}>...</CDS.Modal>"}</code>
        </Text>
      </Box>
    </CDS.Stack>
  );
}

function ControlsExamples() {
  const toast = useToast();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: text,
      status: "success",
      duration: 2000,
      size: "sm",
    });
  };

  interface ComponentShowcaseProps {
    title: string;
    description: string;
    codeSnippet: string;
    children: React.ReactNode;
  }

  function ComponentShowcase({
    title,
    description,
    codeSnippet,
    children,
  }: ComponentShowcaseProps) {
    return (
      <Card size="sm">
        <CardBody>
          <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
            <Box>
              <Heading size="sm" mb={2}>{title}</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                {description}
              </Text>
            </Box>

            <Box
              p={4}
              borderRadius="md"
              border="1px solid"
              borderColor={useColorModeValue("gray.200", "gray.600")}
              bg={useColorModeValue("gray.50", "gray.700")}
              minH="60px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexWrap="wrap"
              gap={3}
            >
              {children}
            </Box>

            <Box>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between} mb={2}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                  Usage
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(codeSnippet)}
                >
                  Copy
                </Button>
              </CDS.Stack>
              <Code fontSize="xs" p={2} w="full" display="block" whiteSpace="pre-wrap">
                {codeSnippet}
              </Code>
            </Box>
          </CDS.Stack>
        </CardBody>
      </Card>
    );
  }

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary}>Controls & Navigation</CDS.Text>
      <CDS.Text variant={CDS.Text.variant.bodyMedium} color={CDS.Color.text.secondary}>
        Interactive control components for navigation, selection, and user input management.
      </CDS.Text>

      <CDS.Grid columns={{ base: 1, lg: 2 }} gap={CDS.Spacing.large}>
        <ComponentShowcase
          title="Tabs"
          description="Interactive tab navigation with multiple variants and design system tokens"
          codeSnippet={`// Basic tabs with line variant
<CDS.Tabs
  items={[
    { label: 'Overview', content: <div>Overview content</div> },
    { label: 'Details', content: <div>Details content</div> },
    { label: 'Settings', content: <div>Settings content</div>, disabled: true }
  ]}
  variant={CDS.Tabs.variant.line}
  size={CDS.Tabs.size.medium}
/>

// Enclosed colored variant
<CDS.Tabs
  items={tabItems}
  variant={CDS.Tabs.variant.enclosedColored}
  defaultIndex={1}
  onChange={(index) => console.log('Tab changed to:', index)}
/>`}
        >
          <CDS.Stack spacing={CDS.Spacing.large} align={CDS.Stack.align.stretch} w="full">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>Line Variant</Text>
              <CDS.Tabs
                items={[
                  { label: "Overview",
                    content: (
                      <Box p={4}>
                        <Text>Overview dashboard with key metrics and charts.</Text>
                        <CDS.Stack direction={CDS.Stack.direction.horizontal} mt={3} spacing={CDS.Spacing.tiny}>
                          <CDS.Tag variant={CDS.Tag.variant.success}>Active</CDS.Tag>
                          <CDS.Tag variant={CDS.Tag.variant.primary}>Premium</CDS.Tag>
                        </CDS.Stack>
                      </Box>
                    ) },
                  { label: "Details",
                    content: (
                      <Box p={4}>
                        <Text>Detailed information and configuration options.</Text>
                        <CDS.Stack mt={3} spacing={CDS.Spacing.tiny} align={CDS.Stack.align.start}>
                          <CDS.UserTag name="John Admin" bgColor={CDS.Color.primitive.red(100)} textColor={CDS.Color.primitive.red(800)} />
                          <CDS.AddButton size={CDS.AddButton.size.small} variant={CDS.AddButton.variant.secondary}>
                            Add Setting
                          </CDS.AddButton>
                        </CDS.Stack>
                      </Box>
                    ) },
                  { label: "Analytics",
                    content: (
                      <Box p={4}>
                        <Text>Analytics and reporting dashboard.</Text>
                        <Text fontSize="sm" color="gray.600" mt={2}>Charts and data visualization would go here.</Text>
                      </Box>
                    ) },
                ]}
                variant={CDS.Tabs.variant.line}
                size={CDS.Tabs.size.medium}
              />
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>Soft Rounded Variant</Text>
              <CDS.Tabs
                items={[
                  { label: "Users",
                    content: (
                      <Box p={3}>
                        <Text fontSize="sm">User management interface</Text>
                      </Box>
                    ) },
                  { label: "Roles",
                    content: (
                      <Box p={3}>
                        <Text fontSize="sm">Role and permission settings</Text>
                      </Box>
                    ) },
                  { label: "Settings",
                    content: (
                      <Box p={3}>
                        <Text fontSize="sm">General system settings</Text>
                      </Box>
                    ) },
                ]}
                variant={CDS.Tabs.variant.softRounded}
                size={CDS.Tabs.size.small}
              />
            </Box>
          </CDS.Stack>
        </ComponentShowcase>

        <ComponentShowcase
          title="SectionSelector"
          description="Navigation component for selecting between different sections with labels and item counts"
          codeSnippet={`// Basic section selector
<CDS.SectionSelector
  sections={[
    { id: 'home', name: 'Home', items: 5 },
    { id: 'about', name: 'About', items: 3 },
    { id: 'contact', name: 'Contact', items: 1 }
  ]}
  selectedSection={selectedSection}
  onSectionChange={setSelectedSection}
/>

// With descriptions and custom variant
<CDS.SectionSelector
  sections={[
    {
      id: 'foundation',
      name: 'Foundation',
      description: 'Core design tokens',
      items: 5
    },
    {
      id: 'components',
      name: 'Components',
      description: 'UI building blocks',
      items: 12
    }
  ]}
  selectedSection={selectedSection}
  onSectionChange={setSelectedSection}
/>`}
        >
          <CDS.Stack spacing={CDS.Spacing.large} align={CDS.Stack.align.stretch} w="full">
            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>Subtle Variant (Default)</Text>
              <Box maxW="300px">
                <CDS.SectionSelector
                  sections={[
                    { id: "foundation", name: "Foundation", description: "Core design tokens", items: 5 },
                    { id: "components", name: "Components", description: "UI building blocks", items: 12 },
                    { id: "patterns", name: "Patterns", description: "Complex layouts", items: 3 },
                  ]}
                  selectedSection="foundation"
                  onSectionChange={(id) => console.log("Selected:", id)}
                />
              </Box>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={3}>Outline Variant</Text>
              <Box maxW="300px">
                <CDS.SectionSelector
                  sections={[
                    { id: "dashboard", name: "Dashboard", items: 8 },
                    { id: "analytics", name: "Analytics", items: 15 },
                    { id: "settings", name: "Settings", items: 6, disabled: true },
                  ]}
                  selectedSection="analytics"
                  onSectionChange={(id) => console.log("Selected:", id)}
                />
              </Box>
            </Box>
          </CDS.Stack>
        </ComponentShowcase>

        <ComponentShowcase
          title="Controls Coming Soon"
          description="Additional interactive controls for user input management"
          codeSnippet={`// Pagination component
<CDS.Pagination
  currentPage={1}
  totalPages={10}
  onPageChange={handlePageChange}
/>

// Date picker
<CDS.DatePicker
  value={selectedDate}
  onChange={setSelectedDate}
  placeholder="Select date"
/>

// Multi-select dropdown
<CDS.MultiSelect
  options={options}
  value={selectedItems}
  onChange={setSelectedItems}
  placeholder="Select items"
/>`}
        >
          <CDS.Stack align={CDS.Stack.align.center} spacing={CDS.Spacing.large}>
            <CDS.Text variant={CDS.Text.variant.titleMedium} color={CDS.Color.text.primary}>
              More Controls Coming Soon
            </CDS.Text>

            <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.medium}>
              <Button size="sm" variant="outline" isDisabled>
                Pagination
              </Button>
              <Button size="sm" variant="outline" isDisabled>
                Date Picker
              </Button>
              <Button size="sm" variant="outline" isDisabled>
                Multi-Select
              </Button>
              <Button size="sm" variant="outline" isDisabled>
                Search Control
              </Button>
            </CDS.Stack>

            <CDS.Text variant={CDS.Text.variant.caption} color={CDS.Color.text.tertiary}>
              These components are planned for future releases
            </CDS.Text>
          </CDS.Stack>
        </ComponentShowcase>
      </CDS.Grid>
    </CDS.Stack>
  );
}

function SpecializedComponents() {
  const toast = useToast();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: text,
      status: "success",
      duration: 2000,
      size: "sm",
    });
  };

  interface ComponentShowcaseProps {
    title: string;
    description: string;
    codeSnippet: string;
    children: React.ReactNode;
  }

  function ComponentShowcase({
    title,
    description,
    codeSnippet,
    children,
  }: ComponentShowcaseProps) {
    return (
      <Card size="sm">
        <CardBody>
          <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.medium}>
            <Box>
              <Heading size="sm" mb={2}>{title}</Heading>
              <Text fontSize="sm" color="gray.600" mb={3}>
                {description}
              </Text>
            </Box>

            <Box
              p={4}
              borderRadius="md"
              border="1px solid"
              borderColor={useColorModeValue("gray.200", "gray.600")}
              bg={useColorModeValue("gray.50", "gray.700")}
              minH="60px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexWrap="wrap"
              gap={3}
            >
              {children}
            </Box>

            <Box>
              <CDS.Stack direction={CDS.Stack.direction.horizontal} justify={CDS.Stack.justify.between} mb={2}>
                <Text fontSize="xs" fontWeight="semibold" color="gray.500" textTransform="uppercase">
                  Usage
                </Text>
                <Button
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(codeSnippet)}
                >
                  Copy
                </Button>
              </CDS.Stack>
              <Code fontSize="xs" p={2} w="full" display="block" whiteSpace="pre-wrap">
                {codeSnippet}
              </Code>
            </Box>
          </CDS.Stack>
        </CardBody>
      </Card>
    );
  }

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Text variant={CDS.Text.variant.headlineSmall} color={CDS.Color.text.primary}>Specialized Components</CDS.Text>
      <CDS.Text variant={CDS.Text.variant.bodyMedium} color={CDS.Color.text.secondary}>
        These components are built for specific use cases with consistent styling and behavior. They automatically adapt to light/dark modes.
      </CDS.Text>

      <CDS.Grid columns={{ base: 1, lg: 2 }} gap={CDS.Spacing.large}>
        <ComponentShowcase
          title="AddButton"
          description="Specialized button for adding items with consistent styling and icon"
          codeSnippet={`<CDS.AddButton>Add Item</CDS.AddButton>
<CDS.AddButton variant={CDS.AddButton.variant.secondary}>
  Add User
</CDS.AddButton>
<CDS.AddButton
  variant={CDS.AddButton.variant.ghost}
  size={CDS.AddButton.size.small}
>
  Add
</CDS.AddButton>`}
        >
          <CDS.AddButton>Add Item</CDS.AddButton>
          <CDS.AddButton variant={CDS.AddButton.variant.secondary}>
            Add User
          </CDS.AddButton>
          <CDS.AddButton
            variant={CDS.AddButton.variant.ghost}
            size={CDS.AddButton.size.small}
          >
            Add
          </CDS.AddButton>
        </ComponentShowcase>

        <ComponentShowcase
          title="UserTag"
          description="User tag with customizable colors using design system tokens"
          codeSnippet={`<CDS.UserTag name="John Doe" />
<CDS.UserTag
  name="Jane Smith"
  bgColor={CDS.Color.primitive.blue(100)}
  textColor={CDS.Color.primitive.blue(800)}
  borderColor={CDS.Color.primitive.blue(200)}
/>
<CDS.UserTag
  name="Mike Wilson"
  bgColor={CDS.Color.primitive.red(100)}
  textColor={CDS.Color.primitive.red(800)}
/>
<CDS.UserTag
  name="Sarah Chen"
  bgColor={CDS.Color.primitive.green(100)}
  textColor={CDS.Color.primitive.green(800)}
/>`}
        >
          <CDS.UserTag name="John Doe" />
          <CDS.UserTag
            name="Jane Smith"
            bgColor={CDS.Color.primitive.blue(100)}
            textColor={CDS.Color.primitive.blue(800)}
            borderColor={CDS.Color.primitive.blue(200)}
          />
          <CDS.UserTag
            name="Mike Wilson"
            bgColor={CDS.Color.primitive.red(100)}
            textColor={CDS.Color.primitive.red(800)}
          />
          <CDS.UserTag
            name="Sarah Chen"
            bgColor={CDS.Color.primitive.green(100)}
            textColor={CDS.Color.primitive.green(800)}
          />
        </ComponentShowcase>

        <ComponentShowcase
          title="External Component Integration"
          description="Using CDS resolver hooks with external components like Chakra UI"
          codeSnippet={`// Import resolver hooks
import { useResolveColor, useResolveSpacing, useResolveSize } from '../design-system';

// Use in external components
const ExternalComponent = () => {
  const bg = useResolveColor(CDS.Color.surface.secondary);
  const padding = useResolveSpacing(CDS.Spacing.medium);
  const width = useResolveSize(CDS.Size.percent(75));

  return (
    <Box
      bg={bg.value}
      p={padding.value}
      w={width.value}
      borderRadius="md"
    >
      External component using CDS tokens!
    </Box>
  );
};

// Available length values
CDS.Size.percent(100)  // 100%
CDS.Size.px(200)       // 200px
CDS.Size.rem(1.5)      // 1.5rem
CDS.Size.full          // 100%
CDS.Size.half          // 50%
CDS.Size.auto          // auto`}
        >
          <ExternalIntegrationExample />
        </ComponentShowcase>

        <ComponentShowcase
          title="CDS Component Styling"
          description="CDS components now accept common styling props like padding, margin, width, etc."
          codeSnippet={`// CDS components with styling props
<CDS.Stack
  p={CDS.Spacing.large}
  bg={CDS.Color.surface.secondary}
  borderRadius={CDS.Spacing.small}
  w={CDS.Size.full}
>
  <CDS.Divider
    mx={CDS.Spacing.medium}
    w={CDS.Size.percent(80)}
  />
</CDS.Stack>

// Available styling props:
// Padding: p, pt, pr, pb, pl, px, py, padding, etc.
// Margin: m, mt, mr, mb, ml, mx, my, margin, etc.
// Sizing: w, h, minW, maxW, minH, maxH, width, height
// Background: bg, backgroundColor
// Border: borderColor, borderWidth, borderRadius (uses CDS.Radius)`}
        >
          <CDSComponentStylingExample />
        </ComponentShowcase>

        <ComponentShowcase
          title="Corner Radius System"
          description="Semantic corner radius values built on our spacing foundation"
          codeSnippet={`// Semantic corner radius values
CDS.Radius.none        // 0px (sharp corners)
CDS.Radius.tiny        // 4px
CDS.Radius.small       // 8px
CDS.Radius.medium      // 12px
CDS.Radius.large       // 16px
CDS.Radius.extraLarge  // 20px
CDS.Radius.huge        // 24px
CDS.Radius.full        // 48px (pill shapes)

// Semantic aliases for components
CDS.Radius.button      // 8px
CDS.Radius.card        // 12px
CDS.Radius.input       // 8px
CDS.Radius.modal       // 16px
CDS.Radius.tag         // 4px
CDS.Radius.avatar      // 8px

// Usage with CDS components
<CDS.Stack borderRadius={CDS.Radius.card}>
  Card-style container
</CDS.Stack>`}
        >
          <RadiusExample />
        </ComponentShowcase>
      </CDS.Grid>
    </CDS.Stack>
  );
}

function RadiusExample() {
  return (
    <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch} w="full">
      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={3}>Size Variants</Text>
        <CDS.Grid columns={{ base: 2, md: 4 }} gap={CDS.Spacing.small}>
          {[
            { name: "None", value: CDS.Radius.none },
            { name: "Tiny", value: CDS.Radius.tiny },
            { name: "Small", value: CDS.Radius.small },
            { name: "Medium", value: CDS.Radius.medium },
            { name: "Large", value: CDS.Radius.large },
            { name: "Extra Large", value: CDS.Radius.extraLarge },
            { name: "Huge", value: CDS.Radius.huge },
            { name: "Full", value: CDS.Radius.full },
          ].map((item) => (
            <CDS.Stack
              key={item.name}
              p={CDS.Spacing.medium}
              bg={CDS.Color.surface.secondary}
              borderRadius={item.value}
              align={CDS.Stack.align.center}
              minH={CDS.Size.px(60)}
            >
              <Text fontSize="xs" textAlign="center">
                {item.name}
              </Text>
            </CDS.Stack>
          ))}
        </CDS.Grid>
      </Box>

      <Box>
        <Text fontSize="sm" fontWeight="medium" mb={3}>Semantic Aliases</Text>
        <CDS.Grid columns={{ base: 2, md: 3 }} gap={CDS.Spacing.small}>
          {[
            { name: "Button", value: CDS.Radius.button },
            { name: "Card", value: CDS.Radius.card },
            { name: "Input", value: CDS.Radius.input },
            { name: "Modal", value: CDS.Radius.modal },
            { name: "Tag", value: CDS.Radius.tag },
            { name: "Avatar", value: CDS.Radius.avatar },
          ].map((item) => (
            <CDS.Stack
              key={item.name}
              p={CDS.Spacing.medium}
              bg={CDS.Color.surface.tertiary}
              borderRadius={item.value}
              align={CDS.Stack.align.center}
              minH={CDS.Size.px(60)}
            >
              <Text fontSize="xs" textAlign="center">
                {item.name}
              </Text>
            </CDS.Stack>
          ))}
        </CDS.Grid>
      </Box>

      <Text fontSize="xs" color="gray.600" fontStyle="italic">
        All corner radius values are based on our spacing foundation for consistency
      </Text>
    </CDS.Stack>
  );
}

function CDSComponentStylingExample() {
  return (
    <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch} w="full">
      <CDS.Stack
        p={CDS.Spacing.large}
        bg={CDS.Color.surface.secondary}
        borderRadius={CDS.Radius.card}
        w={CDS.Size.full}
      >
        <Text fontSize="sm" fontWeight="medium">
          CDS.Stack with padding, background, border radius, and full width
        </Text>

        <CDS.Divider
          mx={CDS.Spacing.medium}
          w={CDS.Size.percent(80)}
          color={CDS.Color.border.primary}
        />

        <Text fontSize="sm">
          CDS.Divider with horizontal margin and 80% width
        </Text>
      </CDS.Stack>

      <CDS.Stack
        direction={CDS.Stack.direction.horizontal}
        spacing={CDS.Spacing.small}
        w={CDS.Size.full}
      >
        <CDS.Stack
          p={CDS.Spacing.medium}
          bg={CDS.Color.surface.tertiary}
          borderRadius={CDS.Radius.button}
          w={CDS.Size.half}
          minH={CDS.Size.px(100)}
        >
          <Text fontSize="sm">Half width with button radius</Text>
        </CDS.Stack>

        <CDS.Stack
          p={CDS.Spacing.small}
          bg={CDS.Color.surface.quaternary}
          borderRadius={CDS.Radius.tag}
          w={CDS.Size.quarter}
        >
          <Text fontSize="xs">Quarter width with tag radius</Text>
        </CDS.Stack>
      </CDS.Stack>

      <Text fontSize="xs" color="gray.600" fontStyle="italic">
        All styling above uses CDS tokens applied directly to CDS components
      </Text>
    </CDS.Stack>
  );
}

function ExternalIntegrationExample() {
  // Demonstrate resolver hooks with external Chakra components
  const primaryBg = useResolveColor(CDS.Color.surface.secondary);
  const secondaryBg = useResolveColor(CDS.Color.surface.tertiary);
  const textColor = useResolveColor(CDS.Color.text.primary);
  const largePadding = useResolveSpacing(CDS.Spacing.large);
  const mediumPadding = useResolveSpacing(CDS.Spacing.medium);
  const fullWidth = useResolveSize(CDS.Size.full);
  const halfWidth = useResolveSize(CDS.Size.half);
  const quarterWidth = useResolveSize(CDS.Size.quarter);

  return (
    <CDS.Stack spacing={CDS.Spacing.medium} align={CDS.Stack.align.stretch} w="full">
      <Box
        bg={primaryBg.value}
        p={largePadding.value}
        borderRadius="md"
        w={fullWidth.value}
      >
        <Text color={textColor.value} fontWeight="medium" fontSize="sm">
          Full width box using CDS.Color.surface.secondary and CDS.Spacing.large
        </Text>
      </Box>

      <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
        <Box
          bg={secondaryBg.value}
          p={mediumPadding.value}
          borderRadius="md"
          w={halfWidth.value}
        >
          <Text color={textColor.value} fontSize="sm">
            Half width (CDS.Size.half)
          </Text>
        </Box>

        <Box
          bg={secondaryBg.value}
          p={mediumPadding.value}
          borderRadius="md"
          w={quarterWidth.value}
        >
          <Text color={textColor.value} fontSize="sm" noOfLines={2}>
            Quarter width (CDS.Size.quarter)
          </Text>
        </Box>
      </CDS.Stack>

      <Box
        bg={useResolveColor(CDS.Color.border.primary).value}
        height="2px"
        w={useResolveSize(CDS.Size.percent(80)).value}
        borderRadius="full"
      />

      <Text fontSize="xs" color="gray.600" fontStyle="italic">
        All styling above uses CDS tokens resolved through hooks for external Chakra components
      </Text>
    </CDS.Stack>
  );
}

function TableExamples() {
  const sampleData = [
    {
      id: 1, name: "John Doe", email: "john@example.com", role: "Admin", status: "Active",
    },
    {
      id: 2, name: "Jane Smith", email: "jane@example.com", role: "User", status: "Active",
    },
    {
      id: 3, name: "Bob Johnson", email: "bob@example.com", role: "User", status: "Inactive",
    },
  ];

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <Heading size="sm">Table Component</Heading>

      <Card>
        <CardBody p={0}>
          <Table>
            <Thead>
              <Tr>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Role</Th>
                <Th>Status</Th>
                <Th />
              </Tr>
            </Thead>
            <Tbody>
              {sampleData.map((row) => (
                <Tr key={row.id}>
                  <Td>
                    <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.small}>
                      <Avatar size="sm" name={row.name} />
                      <Text fontWeight="medium">{row.name}</Text>
                    </CDS.Stack>
                  </Td>
                  <Td>{row.email}</Td>
                  <Td>
                    <Badge variant={row.role === "Admin" ? "solid" : "outline"}>
                      {row.role}
                    </Badge>
                  </Td>
                  <Td>
                    <Badge colorScheme={row.status === "Active" ? "green" : "gray"}>
                      {row.status}
                    </Badge>
                  </Td>
                  <Td>
                    <Button size="sm" variant="ghost">
                      Edit
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </CardBody>
      </Card>

      <Box>
        <Text fontSize="sm" color="gray.600" fontStyle="italic">
          Code examples:
          {" "}
          <code>{"<CDS.Table data={data} columns={columns} selectable={true} />"}</code>
        </Text>
      </Box>
    </CDS.Stack>
  );
}

export function ComponentsViewer() {
  const [activeTab, setActiveTab] = useState("card");

  const tabs = [
    { id: "card", name: "Card", component: CardExamples },
    { id: "modal", name: "Modal", component: ModalExamples },
    { id: "table", name: "Table", component: TableExamples },
    { id: "controls", name: "Controls", component: ControlsExamples },
    { id: "form", name: "Form", component: () => <Text>Form examples coming soon...</Text> },
    { id: "specialized", name: "Specialized Components", component: SpecializedComponents },
  ];

  const ActiveComponent = tabs.find((tab) => tab.id === activeTab)?.component;

  return (
    <CDS.Stack align={CDS.Stack.align.stretch} spacing={CDS.Spacing.large}>
      <CDS.Stack direction={CDS.Stack.direction.horizontal} spacing={CDS.Spacing.tiny}>
        {tabs.map((tab) => (
          <Button
            key={tab.id}
            size="sm"
            variant={activeTab === tab.id ? "solid" : "ghost"}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.name}
          </Button>
        ))}
      </CDS.Stack>

      <CDS.Divider />

      <Box>
        {ActiveComponent && <ActiveComponent />}
      </Box>
    </CDS.Stack>
  );
}
